import { z } from 'zod';
import { nameSchema, priceSchema } from '../../validation';

/**
 * Per-org pricing override for negotiated enterprise deals.
 *
 * All prices are **per-month rates** in `Subscription.currency`. The
 * billing engine multiplies these by the interval's month count (1 /
 * 6 / 12) when it computes a Billing for the period.
 *
 * Set any subset of fields to override the matching field from the
 * plan catalog; unset fields fall back to the catalog default for the
 * subscription's `interval`.
 */
export const subscriptionPricingOverrideSchema = z.object({
  basePricePerMonth: priceSchema.optional(),
  perVehiclePricePerMonth: priceSchema.optional(),
  overagePerVehiclePerMonth: priceSchema.optional(),
  /**
   * Price of one order — read only on a `per_order` subscription. Not a
   * per-month rate: it is multiplied by the period's order volume, never
   * by the interval's month count.
   */
  perOrderPrice: priceSchema.optional(),
  /** Price of one order beyond the committed volume. */
  overagePerOrderPrice: priceSchema.optional(),
  /**
   * The subscription was sold VAT-inclusive ("99 incl. VAT"). Billing
   * splits the tax out of the period total instead of adding it on top,
   * so the invoice total is the quoted `rate × trucks`.
   */
  taxInclusive: z.boolean().optional(),
  /** Audit note ("Negotiated by sales 2026-05") */
  note: nameSchema.optional(),
});
export type SubscriptionPricingOverride = z.infer<typeof subscriptionPricingOverrideSchema>;

/**
 * Per-addon override (price + feature patch).
 *
 * Allows enterprise deals to (a) get an addon for free, (b) include an
 * addon with no per-org override, or (c) grant the addon's feature
 * without billing. Keyed by addon code.
 *
 * Pricing semantics mirror the catalog: `oneTime` is a single-shot
 * total, while `month` / `semester` / `year` are **per-month** rates
 * (the billing engine multiplies by 1 / 6 / 12 to get the line total
 * for a recurring billing period).
 */
export const subscriptionAddonOverrideSchema = z.object({
  pricing: z
    .object({
      oneTime: priceSchema.optional(),
      month: priceSchema.optional(),
      semester: priceSchema.optional(),
      year: priceSchema.optional(),
    })
    .optional(),
});
export type SubscriptionAddonOverride = z.infer<typeof subscriptionAddonOverrideSchema>;
